/**
 * IT-UNIFIED-NAV — the single persistent sidebar for the IT Operations Center.
 * Used inside ITDashboard (tab mode: onTabSelect) and by AdminLayout on every
 * other IT-role route (standalone mode: tab items deep-link back to the
 * dashboard via /admin/it-dashboard?tab=...).
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, Ticket, CheckSquare, Megaphone, Package, Key, ZapOff,
  FileText, Shield, Users, UserCheck, ChevronsLeft, ChevronsRight, ArrowLeft, PenLine
} from 'lucide-react';
import { getUserFromToken } from '../../utils/adminApi';
import './ITSidebar.css';

const ITSidebar = ({ activeTab = null, onTabSelect = null, isITManager = false, standalone = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem('it-sidebar-collapsed') === '1');
  const isAdmin = getUserFromToken()?.role === 'admin';

  const toggle = () => setCollapsed(prev => {
    localStorage.setItem('it-sidebar-collapsed', prev ? '0' : '1');
    return !prev;
  });

  // IT-NAV-ALPHA-001: sections and items are listed alphabetically (ordering only; routes/permissions unchanged)
  const groups = [
    {
      section: 'Administration',
      items: [
        // WORKSPACE-NAV-VIS-001 (Oct 2026): hidden for the logged-in admin / super admin role only;
        // IT Manager / IT Support keep their personal workspace (role-based, not portal-based).
        ...(!isAdmin ? [{ id: 'workspace', label: 'My Workspace', subtitle: 'Personal HR & Support Hub', icon: UserCheck, route: '/admin/workspace' }] : []),
        { id: 'users', label: 'Users', icon: Users, route: '/admin/users' },
      ]
    },
    {
      section: 'IT Operations',
      items: [
        { id: 'announcements', label: 'Announcements', icon: Megaphone },
        { id: 'assets', label: 'Assets', icon: Package },
        { id: 'credentials', label: 'Credentials', icon: Key },
        { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'downtime', label: 'Downtime', icon: ZapOff },
        { id: 'requests', label: 'IT Requests', icon: Ticket },
        { id: 'tasks', label: 'Tasks', icon: CheckSquare },
      ]
    },
    ...(isITManager ? [{
      section: 'Reporting',
      items: [
        { id: 'audit', label: 'Audit', icon: Shield },
        { id: 'reports', label: 'Reports', icon: FileText },
      ]
    }, {
      section: 'Team',
      items: [
        // TEAM-SHELL-001: opens inside the IT Ops shell at /admin/team
        { id: 'team-management', label: 'Team Management', subtitle: 'Leave approvals & availability', icon: Users, route: '/admin/team' },
      ]
    }, {
      section: 'Training',
      items: [
        // TRAINING-WB-001: it_manager only; opens the shared ephemeral whiteboard
        { id: 'training-whiteboard', label: 'Training Whiteboard', subtitle: 'Ephemeral drawing canvas', icon: PenLine, route: '/training/whiteboard', query: '?from=it' },
      ]
    }] : []),
  ];

  const handleClick = (item) => {
    if (item.route) {
      navigate(item.route + (item.query || ''));
    } else if (onTabSelect) {
      onTabSelect(item.id);
    } else {
      navigate(`/admin/it-dashboard?tab=${item.id}`);
    }
  };

  const isActive = (item) => {
    if (item.route) return location.pathname.startsWith(item.route);
    return onTabSelect ? activeTab === item.id : false;
  };

  return (
    <aside className={`it-sidebar ${collapsed ? 'collapsed' : ''} ${standalone ? 'standalone' : ''}`} data-testid="it-sidebar">
      <div>
        <div className="it-sidebar-brand" data-testid="it-sidebar-brand">
          <div className="it-brand-icon">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"/></svg>
          </div>
          {!collapsed && (
            <div className="it-brand-text">
              <span className="it-brand-name">TitleFlow</span>
              <span className="it-brand-sub">IT Portal</span>
            </div>
          )}
        </div>
        {isAdmin && (
          <button
            className="it-admin-home"
            onClick={() => navigate('/admin')}
            title="Return to the TitleFlow Admin Portal"
            data-testid="it-back-to-admin"
          >
            <ArrowLeft size={15} />
            {!collapsed && <span>Admin Home</span>}
          </button>
        )}
        {groups.map(group => (
          <div key={group.section} className="it-sidenav-group">
            {!collapsed && <div className="it-sidenav-section">{group.section}</div>}
            <nav className="it-sidenav">
              {group.items.map(item => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    className={`it-sidenav-item ${isActive(item) ? 'active' : ''} ${item.subtitle ? 'with-subtitle' : ''}`}
                    onClick={() => handleClick(item)}
                    title={collapsed ? item.label : undefined}
                    data-testid={`sidenav-${item.id}`}
                  >
                    <Icon size={17} />
                    {!collapsed && (
                      <span className="it-sidenav-label">
                        {item.label}
                        {item.subtitle && <small>{item.subtitle}</small>}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        ))}
      </div>
      <button
        className="it-sidebar-toggle"
        onClick={toggle}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        data-testid="sidebar-toggle-btn"
      >
        {collapsed ? <ChevronsRight size={16} /> : <><ChevronsLeft size={16} /><span>Collapse</span></>}
      </button>
    </aside>
  );
};

export default ITSidebar;
